import React from 'react';
import { Box, Typography, Stack, Link } from '@mui/material';
import Section from './Section';
import amazon_logo from '../img/logo_amazon.svg';
import doordash_logo from '../img/logo_doordash.svg';
import google_logo from '../img/logo_google.svg';
import stanford_logo from '../img/stanford_logo.png';
import gt_logo from '../img/logo_georgia_tech.png';

const ExtLink = ({ href, children }) => (
  <Link href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </Link>
);

const research = [
  {
    org: 'Stanford Intelligent Systems Laboratory (SISL)',
    subtitle: <>Advised by <ExtLink href="https://mykel.kochenderfer.com/">Dr. Mykel Kochenderfer</ExtLink></>,
    period: 'Sep 2026 – Present',
    icon: stanford_logo,
    question: 'How can a robot tell when its failure detector is no longer trustworthy?',
    description:
      'I developed martingale-triggered recalibration for world-model failure detectors: a conformal test martingale over successful episodes detects when distribution shift voids the false-alarm guarantee and triggers recalibration. I evaluated it on real bimanual LEGO-assembly episodes under lighting, camera, proprioceptive, and task-switch shifts, comparing against six baselines including PID, ACI, and an oracle.',
  },
  {
    org: 'Golub Capital Social Impact Lab, Stanford University',
    subtitle: <>Advised by <ExtLink href="https://sites.google.com/stanford.edu/yuyan-wang">Dr. Yuyan Wang</ExtLink></>,
    period: 'Sep 2025 – Present',
    icon: stanford_logo,
    question: 'How can a platform learn what each child wants to read, and which stories its catalog is still missing?',
    description:
      "For a children's story app, I built fixed-effects and two-tower recommendation models and explored deep interest network variants that model long-term intent alongside short-term context. I am also adapting Lloyd's algorithm to use recommender embeddings to find gaps in the content catalog.",
  },
  {
    org: 'Georgia Institute of Technology',
    subtitle: (
      <>
        Advised by <ExtLink href="https://www.cse.gatech.edu/people/mahdi-roozbahani">Dr. Mahdi Roozbahani</ExtLink> and{' '}
        <ExtLink href="https://ce.gatech.edu/directory/person/j-david-frost">Dr. J. David Frost</ExtLink>
      </>
    ),
    period: 'Jan 2024 – May 2025',
    icon: gt_logo,
    logoSize: '82%',
    question: 'How can social media images and videos be turned into reliable damage assessments right after an earthquake?',
    description:
      'I developed CLIP-based and multimodal models for damage scoring and fine-tuned YOLO on video frames to detect structural damage. I also contributed to two datasets: EID (Earthquake Spectra 2025) and EIDSeg (AAAI 2026).',
  },
];

const industry = [
  {
    org: 'Google',
    role: 'Software Engineer Intern, Recommender Systems and Debiasing',
    period: 'Summer 2026',
    icon: google_logo,
    description:
      'Quantified position bias in a two-tower retrieval model and corrected exposure bias with inverse propensity scoring and shallow-tower debiasing.',
  },
  {
    org: 'DoorDash',
    role: 'Software Engineer Intern, Fraud Detection and Financial ML',
    period: 'Summers 2024, 2025',
    icon: doordash_logo,
    description:
      'Vision Transformer–based fraud detection on refund images; LightGBM and DBSCAN for automated financial variance analysis.',
  },
  {
    org: 'Amazon',
    role: 'Software Engineer Intern, Generative AI and Computer Vision',
    period: '2023, 2024',
    icon: amazon_logo,
    description:
      'LLM-based synthesis of customer feedback; image classification for content moderation in promotional emails.',
  },
];

// size: share of the tile the logo fills; round seals need more to look as big as the others.
const Logo = ({ src, alt, size = '68%' }) => (
  <Box
    sx={{
      width: { xs: 40, sm: 52 },
      height: { xs: 40, sm: 52 },
      flexShrink: 0,
      borderRadius: 2.5,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.paper',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    }}
  >
    <Box component="img" src={src} alt={alt} sx={{ width: size, height: size, objectFit: 'contain' }} />
  </Box>
);

const Entry = ({ icon, logoSize, title, subtitle, period, question, description }) => (
  <Stack direction="row" spacing={{ xs: 1.5, sm: 2 }} alignItems="flex-start">
    <Logo src={icon} alt={title} size={logoSize} />
    <Box sx={{ flexGrow: 1 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'baseline' }}>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: { sm: 2 } }}>
          {period}
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
        {subtitle}
      </Typography>
      {question && (
        <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 500, mb: 0.5 }}>
          {question}
        </Typography>
      )}
      <Typography variant="body2">{description}</Typography>
    </Box>
  </Stack>
);

export const ResearchExperience = () => (
  <Section id="research" title="Research Experience">
    <Stack spacing={3.5}>
      {research.map((item) => (
        <Entry key={item.org + item.period} title={item.org} {...item} />
      ))}
    </Stack>
  </Section>
);

export const IndustryExperience = () => (
  <Section id="industry" title="Industry Experience">
    <Stack spacing={3}>
      {industry.map((item) => (
        <Entry key={item.org} title={item.org} subtitle={item.role} {...item} />
      ))}
    </Stack>
  </Section>
);
